import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  createDevice,
  createTreatment,
  removeBodyAreaImage,
  removeCategoryImage,
  removeDeviceImage,
  removeTreatmentImage,
  replaceBodyAreas,
  replaceTreatmentCategories,
  setDeviceActive,
  setTreatmentActive,
  updateDevice,
  updateTreatment,
  uploadBodyAreaImage,
  uploadCategoryImage,
  uploadDeviceImage,
  uploadTreatmentImage,
} from '@/api/treatmentLibrary/treatmentLibraryApi';
import type {
  BodyArea,
  BodyAreaDraft,
  CategoryDraft,
  Device,
  DeviceWritePayload,
  Treatment,
  TreatmentCategory,
  TreatmentWritePayload,
} from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import {
  BODY_AREAS_QUERY_KEY,
  TREATMENTS_QUERY_KEY,
  TREATMENT_CATEGORIES_QUERY_KEY,
  TREATMENT_DEVICES_QUERY_KEY,
} from '@/constants/queryKeys';

function useLibraryInvalidation() {
  const queryClient = useQueryClient();
  return function invalidate() {
    queryClient.invalidateQueries({ queryKey: TREATMENTS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: TREATMENT_CATEGORIES_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: BODY_AREAS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: TREATMENT_DEVICES_QUERY_KEY });
  };
}

interface SaveDeviceVariables {
  deviceId: string | null;
  payload: DeviceWritePayload;
}

export function useSaveDeviceMutation(): UseMutationResult<Device, Error, SaveDeviceVariables> {
  const invalidate = useLibraryInvalidation();
  return useMutation({
    mutationFn: function save(variables: SaveDeviceVariables) {
      if (variables.deviceId === null) {
        return createDevice(variables.payload);
      }
      return updateDevice(variables.deviceId, variables.payload);
    },
    onSuccess: invalidate,
  });
}

interface SetDeviceActiveVariables {
  deviceId: string;
  isActive: boolean;
}

export function useSetDeviceActiveMutation(): UseMutationResult<
  Device,
  Error,
  SetDeviceActiveVariables
> {
  const invalidate = useLibraryInvalidation();
  return useMutation({
    mutationFn: function setActive(variables: SetDeviceActiveVariables) {
      return setDeviceActive(variables.deviceId, variables.isActive);
    },
    onSuccess: invalidate,
  });
}

export function useReplaceCategoriesMutation(): UseMutationResult<
  TreatmentCategory[],
  Error,
  CategoryDraft[]
> {
  const invalidate = useLibraryInvalidation();
  return useMutation({ mutationFn: replaceTreatmentCategories, onSuccess: invalidate });
}

export function useReplaceBodyAreasMutation(): UseMutationResult<BodyArea[], Error, BodyAreaDraft[]> {
  const invalidate = useLibraryInvalidation();
  return useMutation({ mutationFn: replaceBodyAreas, onSuccess: invalidate });
}

interface SaveTreatmentVariables {
  treatmentId: string | null;
  payload: TreatmentWritePayload;
}

export function useSaveTreatmentMutation(): UseMutationResult<
  Treatment,
  Error,
  SaveTreatmentVariables
> {
  const invalidate = useLibraryInvalidation();
  return useMutation({
    mutationFn: function save(variables: SaveTreatmentVariables) {
      if (variables.treatmentId === null) {
        return createTreatment(variables.payload);
      }
      return updateTreatment(variables.treatmentId, variables.payload);
    },
    onSuccess: invalidate,
  });
}

interface SetActiveVariables {
  treatmentId: string;
  isActive: boolean;
}

export function useSetTreatmentActiveMutation(): UseMutationResult<
  Treatment,
  Error,
  SetActiveVariables
> {
  const invalidate = useLibraryInvalidation();
  return useMutation({
    mutationFn: function setActive(variables: SetActiveVariables) {
      return setTreatmentActive(variables.treatmentId, variables.isActive);
    },
    onSuccess: invalidate,
  });
}

interface UploadImageVariables {
  id: string;
  file: File;
}

export function useUploadDeviceImageMutation(): UseMutationResult<Device, Error, UploadImageVariables> {
  const invalidate = useLibraryInvalidation();
  return useMutation({
    mutationFn: function upload(variables: UploadImageVariables) {
      return uploadDeviceImage(variables.id, variables.file);
    },
    onSuccess: invalidate,
  });
}

export function useRemoveDeviceImageMutation(): UseMutationResult<Device, Error, string> {
  const invalidate = useLibraryInvalidation();
  return useMutation({ mutationFn: removeDeviceImage, onSuccess: invalidate });
}

export function useUploadTreatmentImageMutation(): UseMutationResult<
  Treatment,
  Error,
  UploadImageVariables
> {
  const invalidate = useLibraryInvalidation();
  return useMutation({
    mutationFn: function upload(variables: UploadImageVariables) {
      return uploadTreatmentImage(variables.id, variables.file);
    },
    onSuccess: invalidate,
  });
}

export function useRemoveTreatmentImageMutation(): UseMutationResult<Treatment, Error, string> {
  const invalidate = useLibraryInvalidation();
  return useMutation({ mutationFn: removeTreatmentImage, onSuccess: invalidate });
}

export function useUploadCategoryImageMutation(): UseMutationResult<
  TreatmentCategory,
  Error,
  UploadImageVariables
> {
  return useMutation({
    mutationFn: function upload(variables: UploadImageVariables) {
      return uploadCategoryImage(variables.id, variables.file);
    },
  });
}

export function useRemoveCategoryImageMutation(): UseMutationResult<
  TreatmentCategory,
  Error,
  string
> {
  return useMutation({ mutationFn: removeCategoryImage });
}

export function useUploadBodyAreaImageMutation(): UseMutationResult<
  BodyArea,
  Error,
  UploadImageVariables
> {
  return useMutation({
    mutationFn: function upload(variables: UploadImageVariables) {
      return uploadBodyAreaImage(variables.id, variables.file);
    },
  });
}

export function useRemoveBodyAreaImageMutation(): UseMutationResult<BodyArea, Error, string> {
  return useMutation({ mutationFn: removeBodyAreaImage });
}
